<!-- frontend/src/views/CreateActivity.vue -->
<template>
  <div class="create-activity-page">
    <!-- 權限檢查 -->
    <div v-if="!hasPermission" class="no-permission">
      <div class="error-card">
        <div class="error-icon">🚫</div>
        <h2>權限不足</h2>
        <p>只有部門管理員和系統管理員可以訪問此頁面</p>
        <router-link to="/" class="back-btn">返回首頁</router-link>
      </div>
    </div>

    <!-- 活動建立表單 -->
    <div v-else class="create-activity-container">
      <h1>{{ editingId ? '編輯活動申請' : '活動建立申請表' }}</h1>

      <div v-if="editingId" class="editing-banner">
        <div class="editing-banner-row">
          <span>正在編輯申請 #{{ editingId }}</span>
          <button type="button" class="cancel-edit-btn" @click="cancelEdit">取消編輯（改為新建）</button>
        </div>
        <p class="editing-note">提醒：核心能力配置不會隨草稿保存，重新送審前請再次確認總和為 100%。</p>
      </div>

      <form @submit.prevent="submitActivity" class="activity-form">
        <!-- 基本資訊區 -->
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          <div class="form-grid">
            <div class="form-group">
              <label>活動編號</label>
              <input type="text" :value="activityNumber" disabled class="disabled-input">
            </div>
            <div class="form-group">
              <label>活動主辦單位</label>
              <input type="text" :value="department" disabled class="disabled-input">
            </div>
            <div class="form-group">
              <label>承辦人</label>
              <input type="text" :value="organizer" disabled class="disabled-input">
            </div>
            <div class="form-group">
              <label>建檔電話</label>
              <input type="text" :value="phone" disabled class="disabled-input">
            </div>
          </div>
        </div>

        <!-- 活動詳情區 -->
        <div class="form-section">
          <h3 class="section-title">活動詳情</h3>
          <div class="form-grid">
            <div class="form-group col-span-2">
              <label>活動名稱 <span class="required">*</span></label>
              <input type="text" v-model="formData.title" required class="form-input">
            </div>
            <div class="form-group col-span-2">
              <label>活動簡述及目標 <span class="required">*</span></label>
              <div class="rte">
                <div class="rte-toolbar">
                  <button type="button" class="rte-btn" title="粗體" @mousedown.prevent="rteExec('bold')"><b>B</b></button>
                  <label class="rte-btn rte-color-btn" title="文字顏色">
                    <span class="rte-color-a">A</span>
                    <input type="color" class="rte-color-input" @input="rteColor($event)">
                  </label>
                  <span class="rte-sep"></span>
                  <button type="button" class="rte-btn" title="靠左對齊" @mousedown.prevent="rteExec('justifyLeft')">◧</button>
                  <button type="button" class="rte-btn" title="置中對齊" @mousedown.prevent="rteExec('justifyCenter')">▤</button>
                  <button type="button" class="rte-btn" title="靠右對齊" @mousedown.prevent="rteExec('justifyRight')">◨</button>
                  <span class="rte-sep"></span>
                  <button type="button" class="rte-btn rte-btn-text" title="插入連結" @mousedown.prevent="rteInsertLink">🔗 連結</button>
                </div>
                <div
                  ref="rteEditor"
                  class="form-input rte-editor"
                  :class="{ 'is-empty': !formData.description }"
                  contenteditable="true"
                  data-placeholder="請簡述活動內容與預期達成的目標"
                  @input="syncRte"
                  @blur="syncRte"
                  @mouseup="saveRteSelection"
                  @keyup="saveRteSelection"
                ></div>
              </div>
            </div>
            <div class="form-group col-span-2">
              <label class="checkbox-label">
                <input type="checkbox" v-model="formData.multiDay">
                <span>跨多日活動（開始與結束為不同日期）</span>
              </label>
            </div>

            <!-- 單日活動 -->
            <template v-if="!formData.multiDay">
              <div class="form-group">
                <label>活動日期 <span class="required">*</span></label>
                <input type="date" v-model="formData.activityDate" required class="form-input">
              </div>
              <div class="form-group">
                <label>活動時間 <span class="required">*</span></label>
                <div class="time-inputs">
                  <span class="time-picker">
                    <select v-model="startHour" required class="form-input time-sel"><option value="">時</option><option v-for="h in hourOptions" :key="'sdsh'+h" :value="h">{{ h }}</option></select>
                    <span class="colon">:</span>
                    <select v-model="startMinute" required class="form-input time-sel"><option value="">分</option><option v-for="m in minuteOptions" :key="'sdsm'+m" :value="m">{{ m }}</option></select>
                  </span>
                  <span class="time-separator">至</span>
                  <span class="time-picker">
                    <select v-model="endHour" required class="form-input time-sel"><option value="">時</option><option v-for="h in hourOptions" :key="'sdeh'+h" :value="h">{{ h }}</option></select>
                    <span class="colon">:</span>
                    <select v-model="endMinute" required class="form-input time-sel"><option value="">分</option><option v-for="m in minuteOptions" :key="'sdem'+m" :value="m">{{ m }}</option></select>
                  </span>
                </div>
              </div>
            </template>

            <!-- 跨多日活動：開始／結束各自年月日 + 時分 -->
            <template v-else>
              <div class="form-group">
                <label>活動開始（年月日 時分）<span class="required">*</span></label>
                <div class="time-inputs">
                  <input type="date" v-model="formData.activityDate" required class="form-input">
                  <span class="time-picker">
                    <select v-model="startHour" required class="form-input time-sel"><option value="">時</option><option v-for="h in hourOptions" :key="'mdsh'+h" :value="h">{{ h }}</option></select>
                    <span class="colon">:</span>
                    <select v-model="startMinute" required class="form-input time-sel"><option value="">分</option><option v-for="m in minuteOptions" :key="'mdsm'+m" :value="m">{{ m }}</option></select>
                  </span>
                </div>
              </div>
              <div class="form-group">
                <label>活動結束（年月日 時分）<span class="required">*</span></label>
                <div class="time-inputs">
                  <input type="date" v-model="formData.endDate" required class="form-input">
                  <span class="time-picker">
                    <select v-model="endHour" required class="form-input time-sel"><option value="">時</option><option v-for="h in hourOptions" :key="'mdeh'+h" :value="h">{{ h }}</option></select>
                    <span class="colon">:</span>
                    <select v-model="endMinute" required class="form-input time-sel"><option value="">分</option><option v-for="m in minuteOptions" :key="'mdem'+m" :value="m">{{ m }}</option></select>
                  </span>
                </div>
              </div>
            </template>
            <p v-if="!timeRangeValid" class="time-warning">
              ⚠️ 活動結束時間不可早於或等於開始時間，請重新確認。
            </p>
            <div class="form-group col-span-2">
              <label>報名截止時間</label>
              <div class="time-inputs">
                <input type="date" v-model="formData.regDeadlineDate" class="form-input">
                <span class="time-picker">
                  <select v-model="deadlineHour" class="form-input time-sel"><option value="">時</option><option v-for="h in hourOptions" :key="'dh'+h" :value="h">{{ h }}</option></select>
                  <span class="colon">:</span>
                  <select v-model="deadlineMinute" class="form-input time-sel"><option value="">分</option><option v-for="m in minuteOptions" :key="'dm'+m" :value="m">{{ m }}</option></select>
                </span>
              </div>
              <small class="field-hint">留空則以「活動開始時間」為報名截止；截止後不開放報名。</small>
            </div>
            <div class="form-group col-span-2">
              <label>活動地點 <span class="required">*</span></label>
              <input type="text" v-model="formData.location" required class="form-input">
            </div>
            <div class="form-group col-span-2">
              <label>主講人</label>
              <input type="text" v-model="formData.speaker" class="form-input">
            </div>
            <div class="form-group col-span-2">
              <label class="checkbox-label reflection-check">
                <input type="checkbox" v-model="formData.needsReflection">
                <span>此活動需繳交心得（勾選後，報名者可於「心得繳交」頁面上傳心得）</span>
              </label>
            </div>
            <template v-if="formData.needsReflection">
              <div class="form-group">
                <label>心得額外點數 <span class="required">*</span></label>
                <input
                  type="number"
                  v-model.number="formData.reflectionPoints"
                  min="0"
                  max="6"
                  step="1"
                  class="form-input"
                >
                <span class="field-hint">心得審核通過可再獲得的點數，限 0~6 的整數</span>
              </div>
              <div class="form-group">
                <label>心得繳交截止 <span class="required">*</span></label>
                <div class="time-inputs">
                  <span>活動結束後</span>
                  <input
                    type="number"
                    v-model.number="formData.reflectionDays"
                    min="1"
                    max="365"
                    step="1"
                    class="form-input"
                    style="max-width: 100px;"
                  >
                  <span>天內</span>
                </div>
                <span class="field-hint">逾期將無法上傳心得，限 1~365 的整數</span>
              </div>
            </template>
          </div>
        </div>

        <!-- 活動性質與點數 -->
        <div class="form-section">
          <h3 class="section-title">活動性質與點數</h3>
          <div class="form-grid">
            <div class="form-group col-span-2">
              <label>活動性質 <span class="required">*</span></label>
              <select v-model="formData.activityType" @change="updatePoints" required class="form-input">
                <option value="">請選擇</option>
                <option value="huisun">惠蓀講座 (固定2點)</option>
                <option value="other">其他活動</option>
              </select>
            </div>
            <div class="form-group">
              <label>自主學習點數 <span class="required">*</span></label>
              <input
                type="number"
                v-model.number="formData.points"
                min="0"
                max="16"
                step="1"
                @input="validatePoints"
                :disabled="formData.activityType === 'huisun'"
                class="form-input"
                :class="{ 'disabled-input': formData.activityType === 'huisun' }"
              >
              <span class="field-hint">限 0~16 的整數</span>
            </div>
            <div class="form-group">
              <label>預計可參與人數 <span class="required">*</span></label>
              <input type="number" v-model="formData.maxParticipants" required min="1" class="form-input">
            </div>
          </div>
        </div>

        <!-- 學習成效與核心能力 -->
        <div class="form-section">
          <h3 class="section-title">學習成效與核心能力</h3>

          <div class="competency-section">
            <h4>核心能力配置（總和必須為100%）</h4>
            <div class="competency-grid">
              <div v-for="(comp, index) in coreCompetencies" :key="index" class="competency-item">
                <label>
                  <span class="comp-name">{{ comp.name }}</span>
                  <span class="comp-desc">{{ comp.description }}</span>
                </label>
                <div class="percentage-input">
                  <input
                    type="number"
                    v-model="formData.competencyPercentages[index]"
                    @input="validatePercentages"
                    min="0"
                    max="100"
                    class="form-input"
                  >
                  <span class="percentage-symbol">%</span>
                </div>
              </div>
            </div>
            <div class="percentage-total" :class="{ 'error': percentageTotal !== 100, 'success': percentageTotal === 100 }">
              總和：{{ percentageTotal }}%
              <span v-if="percentageTotal !== 100" class="error-msg">（須為100%才能送出）</span>
            </div>
          </div>
        </div>

        <!-- 活動關鍵字 -->
        <div class="form-section">
          <h3 class="section-title">活動關鍵字</h3>

          <!-- UCAN -->
          <div class="keyword-group">
            <h4>UCAN 職能</h4>
            <div class="checkbox-grid">
              <label v-for="keyword in ucanKeywords" :key="keyword" class="checkbox-label">
                <input type="checkbox" :value="keyword" v-model="formData.ucanKeywords">
                <span>{{ keyword }}</span>
              </label>
            </div>
          </div>

          <!-- USR -->
          <div class="keyword-group">
            <h4>USR 社會實踐</h4>
            <div class="checkbox-grid">
              <label v-for="keyword in usrKeywords" :key="keyword" class="checkbox-label">
                <input type="checkbox" :value="keyword" v-model="formData.usrKeywords">
                <span>{{ keyword }}</span>
              </label>
            </div>
          </div>

          <!-- SDGs -->
          <div class="keyword-group">
            <h4>SDGs 永續發展目標</h4>
            <div class="checkbox-grid">
              <label v-for="(keyword, index) in sdgsKeywords" :key="keyword" class="checkbox-label">
                <input type="checkbox" :value="keyword" v-model="formData.sdgsKeywords">
                <span>{{ index + 1 }}. {{ keyword }}</span>
              </label>
            </div>
          </div>

          <!-- 自訂關鍵字 -->
          <div class="custom-keywords">
            <label>自訂關鍵字（以逗號分隔）</label>
            <input type="text" v-model="formData.customKeywords" placeholder="例如：AI, 創新, 永續" class="form-input">
          </div>
        </div>

        <!-- 報名方式 -->
        <div class="form-section">
          <h3 class="section-title">報名方式與設備需求</h3>
          
          <!-- 報名方式：目前僅開放使用自主學習報名系統（外部報名網址暫時隱藏） -->
          <div class="form-group">
            <label>報名方式</label>
            <p class="reg-note">目前僅開放「使用自主學習報名系統」</p>
          </div>

          <div class="form-group">
            <label>電子表單檢視設備借用日期</label>
            <div class="equipment-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="formData.noEquipment">
                <span>無須使用</span>
              </label>
              <div v-if="!formData.noEquipment" class="date-inputs">
                <input type="date" v-model="formData.equipmentDate" class="form-input">
                <select v-model="formData.equipmentTime" class="form-input">
                  <option value="">請選擇時段</option>
                  <option value="morning">09:00-12:00</option>
                  <option value="afternoon">13:30-17:00</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- 申請流程說明 -->
        <div class="form-section info-section">
          <h3 class="section-title">申請流程說明</h3>
          <ol class="process-list">
            <li>活動開始十個工作天前提出線上申請</li>
            <li>電子表單檢視設備借用時段為 09:00-12:00 或 13:30-17:00</li>
          </ol>
        </div>

        <!-- 送出按鈕 -->
        <div class="form-actions">
          <button type="button" @click="saveDraft" class="btn-secondary">
            儲存草稿
          </button>
          <button type="button" @click="openPreview" class="btn-preview">
            預覽頁面
          </button>
          <button
            type="submit"
            class="btn-primary"
            :disabled="!isFormValid"
          >
            {{ editingId ? '重新送審' : '送出申請' }}
          </button>
        </div>
      </form>

      <!-- ===== 活動頁面預覽視窗 ===== -->
      <div v-if="showPreview" class="preview-overlay" @click.self="showPreview = false">
        <div class="preview-modal">
          <div class="preview-head">
            <h3>活動頁面預覽</h3>
            <button type="button" class="preview-close" @click="showPreview = false">✕</button>
          </div>
          <p class="preview-note">此為送出後 activity 頁面的呈現預覽，供確認文字排版；實際送出仍需通過審核。</p>
          <div class="preview-body">
            <h2 class="preview-title">{{ formData.title || '（未填活動名稱）' }}</h2>
            <table class="preview-table">
              <tbody>
                <tr><th>活動時間</th><td>{{ previewTimeRange }}</td></tr>
                <tr><th>活動地點</th><td>{{ formData.location || '—' }}</td></tr>
                <tr>
                  <th>活動性質</th>
                  <td>{{ categoryLabel(formData.activityType) }}<span v-if="formData.speaker">，講者：{{ formData.speaker }}</span></td>
                </tr>
                <tr><th>自主學習點數</th><td>{{ formData.points || 0 }} 點</td></tr>
                <tr v-if="formData.description">
                  <th>活動簡述及目標</th>
                  <td class="preview-desc"><div class="rich-content" v-html="safeDescription"></div></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="preview-actions">
            <button type="button" class="btn-secondary" @click="showPreview = false">關閉預覽</button>
          </div>
        </div>
      </div>

      <!-- ===== 區塊二：活動申請追蹤 ===== -->
      <div class="tracking-section">
        <h2 class="tracking-title">活動申請追蹤</h2>
        <p class="tracking-hint">
          此處僅顯示您送出的活動申請。「草稿未送出」可繼續編輯並送出；「駁回」可修改後重新送審；「審核中」與「已通過」不可編輯。
        </p>

        <div v-if="loadingApps" class="tracking-loading">載入中...</div>
        <div v-else-if="myApplications.length === 0" class="tracking-empty">
          目前沒有任何申請或草稿
        </div>
        <div v-else class="tracking-list">
          <div
            v-for="app in myApplications"
            :key="app.id"
            class="tracking-card"
            :class="app.status"
          >
            <div class="tracking-head">
              <h3>{{ app.title || '未命名申請' }}</h3>
              <span class="tracking-badge" :class="app.status">{{ appStatusLabel(app.status) }}</span>
            </div>
            <div class="tracking-meta">
              <span>活動日期：{{ formatDT(app.start_date) }}</span>
              <span>類別：{{ categoryLabel(app.category) }}</span>
              <span>自主學習點數：{{ app.learning_hours }}</span>
            </div>
            <p v-if="app.description" class="tracking-desc">{{ stripHtml(app.description) }}</p>
            <div v-if="app.status === 'rejected' && app.review_comment" class="tracking-comment rejected">
              駁回原因：{{ app.review_comment }}
            </div>
            <div v-else-if="app.review_comment" class="tracking-comment">
              審核意見：{{ app.review_comment }}
            </div>
            <div class="tracking-actions">
              <button
                v-if="app.status === 'draft' || app.status === 'rejected'"
                type="button"
                class="btn-edit"
                :class="{ active: editingId === app.id }"
                @click="editApplication(app)"
              >
                {{ editingId === app.id ? '編輯中' : (app.status === 'rejected' ? '編輯後重新送審' : '編輯草稿') }}
              </button>
              <span v-else class="tracking-locked">
                {{ app.status === 'pending' ? '審核中，暫不可編輯' : '已通過，不可編輯' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createActivity, saveActivityDraft, updateActivityApplication, getMyActivityApplications } from '@/api.js'
import { categoryLabel as catLabel } from '@/utils/category.js'

export default {
  name: 'CreateActivity',
  data() {
    return {
      hasPermission: false,
      activityNumber: '',
      department: '',
      organizer: '',
      phone: '',
      // 活動申請追蹤
      myApplications: [],
      loadingApps: false,
      editingId: null,
      rteRange: null,
      formData: {
        title: '',
        description: '',
        multiDay: false,
        activityDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        regDeadlineDate: '',
        regDeadlineTime: '',
        location: '',
        speaker: '',
        activityType: '',
        points: 2,
        maxParticipants: 50,
        needsReflection: false,
        reflectionPoints: 0,
        reflectionDays: 14,
        ucanKeywords: [],
        usrKeywords: [],
        sdgsKeywords: [],
        customKeywords: '',
        learningOutcome: '',
        competencyPercentages: [0, 0, 0, 0, 0, 0],
        registrationType: 'internal',
        registrationUrl: '',
        noEquipment: true,
        equipmentDate: '',
        equipmentTime: ''
      },
      ucanKeywords: [
        '創新', '問題解決', '人際互動', '工作責任及紀律',
        '持續學習', '團隊合作', '溝通表達', '資訊科技應用'
      ],
      usrKeywords: [
        '在地關懷', '文化永續', '健康促進', '永續環境',
        '產業鏈結與經濟永續', '食品安全', '其他社會實踐'
      ],
      sdgsKeywords: [
        '消除貧窮', '消除飢餓', '健康與福祉', '教育品質',
        '性別平等', '淨水與衛生', '可負擔能源', '就業與經濟成長',
        '工業、創新基礎建設', '減少不平等', '永續城市', '責任消費與生產',
        '氣候行動', '海洋生態', '陸地生態', '和平與正義制度', '全球夥伴'
      ],
      coreCompetencies: [
        { name: '人文素養', description: '培養人文知識涵養與思辨能力' },
        { name: '科學素養', description: '理解及運用科學知識與方法' },
        { name: '溝通能力', description: '有效表達與團隊協作' },
        { name: '創新能力', description: '整合資訊與創新思維' },
        { name: '國際視野', description: '宏觀視角與全球議題理解' },
        { name: '社會關懷', description: '公民素養與社會責任' }
      ],
      approvalStatus: {
        dept: 'pending',
        center: 'pending'
      },
      showPreview: false,
      hourOptions: Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')),
      minuteOptions: Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))
    }
  },
  computed: {
    // 活動時間「時/分」下拉（24 小時制），讀寫 formData.startTime/endTime 的 HH:MM
    startHour: {
      get() { return (this.formData.startTime || '').split(':')[0] || '' },
      set(v) { this.setTimePart('startTime', 'h', v) }
    },
    startMinute: {
      get() { return (this.formData.startTime || '').split(':')[1] || '' },
      set(v) { this.setTimePart('startTime', 'm', v) }
    },
    endHour: {
      get() { return (this.formData.endTime || '').split(':')[0] || '' },
      set(v) { this.setTimePart('endTime', 'h', v) }
    },
    endMinute: {
      get() { return (this.formData.endTime || '').split(':')[1] || '' },
      set(v) { this.setTimePart('endTime', 'm', v) }
    },
    deadlineHour: {
      get() { return (this.formData.regDeadlineTime || '').split(':')[0] || '' },
      set(v) { this.setTimePart('regDeadlineTime', 'h', v) }
    },
    deadlineMinute: {
      get() { return (this.formData.regDeadlineTime || '').split(':')[1] || '' },
      set(v) { this.setTimePart('regDeadlineTime', 'm', v) }
    },
    // 時間防呆：結束不得早於（或等於）開始；未填完整前不判定為錯
    timeRangeValid() {
      const d1 = this.formData.activityDate
      const t1 = this.formData.startTime
      const d2 = this.formData.multiDay ? this.formData.endDate : this.formData.activityDate
      const t2 = this.formData.endTime
      if (!d1 || !/^\d{2}:\d{2}$/.test(t1) || !/^\d{2}:\d{2}$/.test(t2)) return true
      if (this.formData.multiDay && !d2) return true
      const start = new Date(`${d1}T${t1}`)
      const end = new Date(`${d2}T${t2}`)
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return true
      return end > start
    },
    // 預覽用：消毒後的活動簡述（與 activity 詳情頁相同的安全渲染）
    safeDescription() {
      return this.sanitizeHtml(this.formData.description)
    },
    // 預覽用：活動時間字串（24 小時制）
    previewTimeRange() {
      const d1 = this.formData.activityDate
      const t1 = this.formData.startTime
      const d2 = this.formData.multiDay ? this.formData.endDate : this.formData.activityDate
      const t2 = this.formData.endTime
      if (!d1 || !t1) return '—'
      const start = `${d1} ${t1}`
      const end = t2 ? `${d2 || d1} ${t2}` : ''
      return end ? `${start} ~ ${end}` : start
    },
    percentageTotal() {
      return this.formData.competencyPercentages.reduce((sum, val) => sum + (parseInt(val) || 0), 0)
    },
    reflectionFieldsValid() {
      if (!this.formData.needsReflection) return true
      const rp = parseInt(this.formData.reflectionPoints, 10)
      const rd = parseInt(this.formData.reflectionDays, 10)
      return !isNaN(rp) && rp >= 0 && rp <= 6 &&
             !isNaN(rd) && rd >= 1 && rd <= 365
    },
    isFormValid() {
      const pts = parseInt(this.formData.points, 10)
      return this.formData.title &&
             this.formData.description &&
             this.formData.activityDate &&
             (!this.formData.multiDay || this.formData.endDate) &&
             /^\d{2}:\d{2}$/.test(this.formData.startTime) &&
             /^\d{2}:\d{2}$/.test(this.formData.endTime) &&
             this.timeRangeValid &&
             this.formData.location &&
             this.formData.activityType &&
             this.formData.maxParticipants > 0 &&
             !isNaN(pts) && pts >= 0 && pts <= 16 &&
             this.reflectionFieldsValid &&
             this.percentageTotal === 100
    }
  },
  async created() {
    await this.checkPermission()
    this.generateAutoFields()
    if (this.hasPermission) {
      await this.loadMyApplications()
    }
  },
  mounted() {
    // 若有初始內容（理論上為空，保險起見）同步到編輯器 DOM
    this.setRteContent(this.formData.description)
  },
  methods: {
    // 寫入活動時間的時或分（維持 formData.startTime/endTime 為 HH:MM）
    setTimePart(field, part, value) {
      const parts = (this.formData[field] || '').split(':')
      let hh = parts[0] || ''
      let mm = parts[1] || ''
      if (part === 'h') hh = value
      else mm = value
      this.formData[field] = (hh === '' && mm === '') ? '' : `${hh}:${mm}`
    },
    // 開啟活動頁面預覽
    openPreview() {
      this.showPreview = true
    },
    // HTML 消毒（與 ActivityDetail 一致）：只保留編輯器安全標籤/屬性，過濾 script、on*、img、javascript: 等
    sanitizeHtml(html) {
      if (!html) return ''
      const allowed = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'A', 'SPAN', 'DIV', 'P', 'BR', 'FONT', 'UL', 'OL', 'LI'])
      let doc
      try {
        doc = new DOMParser().parseFromString(String(html), 'text/html')
      } catch (e) {
        return ''
      }
      const clean = (node) => {
        for (const child of Array.from(node.childNodes)) {
          if (child.nodeType === 3) continue
          if (child.nodeType !== 1) { child.remove(); continue }
          const tag = child.tagName
          if (!allowed.has(tag)) {
            child.replaceWith(document.createTextNode(child.textContent || ''))
            continue
          }
          for (const attr of Array.from(child.attributes)) {
            const name = attr.name.toLowerCase()
            const val = attr.value
            if (name === 'style') {
              const safe = val.split(';').map(s => s.trim())
                .filter(s => /^(text-align|color)\s*:/i.test(s) && !/url\s*\(|expression|javascript:/i.test(s))
              if (safe.length) child.setAttribute('style', safe.join('; '))
              else child.removeAttribute('style')
              continue
            }
            let keep = false
            if (tag === 'A' && name === 'href') keep = /^(https?:|mailto:)/i.test(val.trim())
            else if (tag === 'A' && (name === 'target' || name === 'rel')) keep = true
            else if (tag === 'FONT' && name === 'color') keep = true
            if (!keep) child.removeAttribute(attr.name)
          }
          if (tag === 'A') {
            child.setAttribute('target', '_blank')
            child.setAttribute('rel', 'noopener noreferrer')
          }
          clean(child)
        }
      }
      clean(doc.body)
      return doc.body.innerHTML
    },
    // ===== 活動簡述及目標 富文本編輯器 =====
    // 記錄目前選取範圍（點工具列按鈕時 restore，避免失焦後 execCommand 失效）
    saveRteSelection() {
      const sel = window.getSelection()
      const el = this.$refs.rteEditor
      if (sel && sel.rangeCount > 0 && el && el.contains(sel.getRangeAt(0).commonAncestorContainer)) {
        this.rteRange = sel.getRangeAt(0).cloneRange()
      }
    },
    restoreRteSelection() {
      const el = this.$refs.rteEditor
      if (!el) return
      el.focus()
      if (this.rteRange) {
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(this.rteRange)
      }
    },
    rteExec(cmd, value = null) {
      this.restoreRteSelection()
      document.execCommand(cmd, false, value)
      this.saveRteSelection()
      this.syncRte()
    },
    rteColor(e) {
      this.rteExec('foreColor', e.target.value)
    },
    rteInsertLink() {
      const url = (window.prompt('請輸入連結網址（需以 http:// 或 https:// 開頭）') || '').trim()
      if (!url) return
      if (!/^https?:\/\//i.test(url)) {
        alert('連結網址必須以 http:// 或 https:// 開頭')
        return
      }
      const name = (window.prompt('請輸入連結顯示文字', url) || url).trim()
      const escName = name.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const escUrl = url.replace(/"/g, '&quot;')
      this.restoreRteSelection()
      document.execCommand('insertHTML', false,
        `<a href="${escUrl}" target="_blank" rel="noopener noreferrer">${escName}</a>&nbsp;`)
      this.saveRteSelection()
      this.syncRte()
    },
    // 同步編輯器 HTML 到 formData.description；內容視覺為空時存空字串（供必填驗證）
    syncRte() {
      const el = this.$refs.rteEditor
      if (!el) return
      const text = (el.textContent || '').replace(/​/g, '').trim()
      this.formData.description = text === '' ? '' : el.innerHTML
    },
    // 以程式方式設定編輯器內容（匯入草稿 / 重置時用）
    setRteContent(html) {
      this.$nextTick(() => {
        if (this.$refs.rteEditor) this.$refs.rteEditor.innerHTML = html || ''
      })
    },
    // 去除 HTML 標籤，供列表/預覽顯示純文字（DOMParser 不執行 script、不載入外部資源）
    stripHtml(html) {
      if (!html) return ''
      try {
        const doc = new DOMParser().parseFromString(String(html), 'text/html')
        return (doc.body.textContent || '').trim()
      } catch (e) {
        return String(html).replace(/<[^>]*>/g, '')
      }
    },
    async checkPermission() {
      // 從後端即時取 profile，確保 is_authorized 是最新（admin 剛 grant 完也生效）
      try {
        const { getProfile } = await import('@/api.js')
        const res = await getProfile()
        const user = res.data
        localStorage.setItem('user_info', JSON.stringify(user))
        // admin 或被賦予部門管理權限（is_authorized=1）的使用者可進
        this.hasPermission = user.role === 'admin' || user.is_authorized === 1
        if (this.hasPermission) {
          this.organizer = user.name
          this.department = user.dept_name || this.getDepartmentName(user.dept_id)
          this.phone = '04-22840000'
        }
      } catch (e) {
        // 若無法取得 profile（例如 token 過期），fallback 讀 localStorage
        const userInfo = localStorage.getItem('user_info')
        if (userInfo) {
          const user = JSON.parse(userInfo)
          this.hasPermission = user.role === 'admin' || user.is_authorized === 1
          if (this.hasPermission) {
            this.organizer = user.name
            this.department = user.dept_name || this.getDepartmentName(user.dept_id)
            this.phone = '04-22840000'
          }
        }
      }
    },
    
    generateAutoFields() {
      // 生成活動編號
      const date = new Date()
      const year = date.getFullYear() - 1911 // 民國年
      const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
      this.activityNumber = `ACT${year}${random}`
    },
    
    getDepartmentName(deptId) {
      const deptMap = {
        1: '通識教育中心',
        2: '資訊工程學系',
        3: '電機工程學系',
        4: '管理學院'
      }
      return deptMap[deptId] || '未設定'
    },
    
    updatePoints() {
      // 惠蓀講座固定 2 點；其他活動點數由承辦人自行設定（0~10 整數）
      if (this.formData.activityType === 'huisun') {
        this.formData.points = 2
      }
    },

    validatePoints() {
      let num = parseInt(this.formData.points, 10)
      if (isNaN(num)) num = 0
      this.formData.points = Math.min(Math.max(num, 0), 16)
    },
    
    validatePercentages() {
      // 確保百分比不超過100
      this.formData.competencyPercentages = this.formData.competencyPercentages.map(val => {
        const num = parseInt(val) || 0
        return Math.min(Math.max(num, 0), 100)
      })
    },
    
    getStatusClass(type) {
      const status = this.approvalStatus[type]
      return {
        'status-pending': status === 'pending',
        'status-approved': status === 'approved',
        'status-rejected': status === 'rejected'
      }
    },
    
    getStatusText(type) {
      const status = this.approvalStatus[type]
      const statusMap = {
        'pending': '待審核',
        'approved': '已通過',
        'rejected': '不予認證'
      }
      return statusMap[status] || '待審核'
    },
    
    // 由表單組出送後端的 payload（草稿與送審共用）
    buildActivityPayload() {
      const allKeywords = [
        ...this.formData.ucanKeywords,
        ...this.formData.usrKeywords,
        ...this.formData.sdgsKeywords,
        ...(this.formData.customKeywords ? this.formData.customKeywords.split(',').map(k => k.trim()) : [])
      ]
      // 組合日期時間：直接送在地時間字串，不用 toISOString()（會轉成 UTC 少 8 小時）
      // 跨多日：結束用 endDate；單日：結束沿用 activityDate
      const endDateStr = this.formData.multiDay ? this.formData.endDate : this.formData.activityDate
      return {
        title: this.formData.title,
        description: this.formData.description,
        activity_date: `${this.formData.activityDate}T${this.formData.startTime}:00`,
        end_date: `${endDateStr}T${this.formData.endTime}:00`,
        location: this.formData.location,
        speaker: this.formData.speaker || null,
        learning_hours: this.formData.points,
        category: this.formData.activityType,
        max_participants: this.formData.maxParticipants,
        reg_deadline: (this.formData.regDeadlineDate && /^\d{2}:\d{2}$/.test(this.formData.regDeadlineTime))
          ? `${this.formData.regDeadlineDate}T${this.formData.regDeadlineTime}:00` : null,
        keywords: allKeywords.join(','),
        registration_url: this.formData.registrationType === 'external' ? this.formData.registrationUrl : null,
        competency_percentages: this.formData.competencyPercentages.join(','),
        needs_reflection: this.formData.needsReflection,
        reflection_points: this.formData.needsReflection ? this.formData.reflectionPoints : null,
        reflection_days: this.formData.needsReflection ? this.formData.reflectionDays : null
      }
    },

    // 草稿至少要有名稱與日期時間，才能組成合法的日期時間字串
    hasMinimalDraftFields() {
      return this.formData.title &&
             this.formData.activityDate &&
             this.formData.startTime &&
             this.formData.endTime &&
             (!this.formData.multiDay || this.formData.endDate)
    },

    async saveDraft() {
      if (!this.hasMinimalDraftFields()) {
        alert('請至少填寫「活動名稱」與「活動日期／時間」後再儲存草稿（跨多日需含結束日期）')
        return
      }
      try {
        const payload = this.buildActivityPayload()
        if (this.editingId) {
          await updateActivityApplication(this.editingId, { ...payload, submit: false })
          alert('草稿已更新')
        } else {
          await saveActivityDraft(payload)
          alert('草稿已儲存')
        }
        this.resetForm()
        await this.loadMyApplications()
      } catch (error) {
        console.error('儲存草稿失敗:', error)
        alert('儲存草稿失敗：' + (error.response?.data?.detail || '請稍後再試'))
      }
    },

    async submitActivity() {
      if (!this.isFormValid) {
        alert('請確認所有必填欄位都已填寫（含活動簡述及目標、點數0~16，跨多日需填結束日期），且核心能力總和為100%')
        return
      }

      try {
        const payload = this.buildActivityPayload()
        if (this.editingId) {
          await updateActivityApplication(this.editingId, { ...payload, submit: true })
          alert('已重新送審，待審核通過後將發送通知')
        } else {
          await createActivity(payload)
          alert('活動申請已送出，待審核通過後將發送通知')
        }
        this.resetForm()
        await this.loadMyApplications()
      } catch (error) {
        console.error('送出活動失敗:', error)
        alert('送出失敗：' + (error.response?.data?.detail || '請稍後再試'))
      }
    },

    // ===== 活動申請追蹤 =====
    async loadMyApplications() {
      this.loadingApps = true
      try {
        const res = await getMyActivityApplications()
        this.myApplications = res.data || []
      } catch (error) {
        console.error('載入申請追蹤失敗:', error)
        this.myApplications = []
      } finally {
        this.loadingApps = false
      }
    },

    appStatusLabel(status) {
      const map = {
        draft: '草稿未送出',
        pending: '審核中',
        rejected: '駁回',
        approved: '已通過'
      }
      return map[status] || status
    },

    categoryLabel(category) {
      return catLabel(category, '未分類')
    },

    formatDT(dateString) {
      if (!dateString) return '待定'
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return dateString
      const p = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())} ${p(date.getHours())}:${p(date.getMinutes())}`
    },

    // 將某筆草稿／被駁回的申請載回表單編輯
    editApplication(app) {
      this.editingId = app.id
      const start = app.start_date ? new Date(app.start_date) : null
      const end = app.end_date ? new Date(app.end_date) : null
      const p = n => String(n).padStart(2, '0')
      const dPart = d => d ? `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` : ''
      const tPart = d => d ? `${p(d.getHours())}:${p(d.getMinutes())}` : ''
      const multiDay = !!(start && end && dPart(start) !== dPart(end))

      this.formData.title = app.title || ''
      this.formData.description = app.description || ''
      this.setRteContent(this.formData.description)
      this.formData.multiDay = multiDay
      this.formData.activityDate = dPart(start)
      this.formData.endDate = multiDay ? dPart(end) : ''
      this.formData.startTime = tPart(start)
      this.formData.endTime = tPart(end)
      const dl = app.reg_deadline ? new Date(app.reg_deadline) : null
      this.formData.regDeadlineDate = dl && !isNaN(dl) ? dPart(dl) : ''
      this.formData.regDeadlineTime = dl && !isNaN(dl) ? tPart(dl) : ''
      this.formData.location = app.location || ''
      this.formData.speaker = app.speaker || ''
      this.formData.activityType = app.category || ''
      this.formData.points = Number.isInteger(app.learning_hours) ? app.learning_hours : (parseInt(app.learning_hours) || 0)
      this.formData.needsReflection = app.needs_reflection === true || app.needs_reflection === 1 || app.needs_reflection === '1'
      this.formData.reflectionPoints = Number.isFinite(app.reflection_points) ? app.reflection_points : (parseInt(app.reflection_points) || 0)
      this.formData.reflectionDays = Number.isFinite(app.reflection_days) ? app.reflection_days : (parseInt(app.reflection_days) || 14)
      // 核心能力／關鍵字未隨草稿保存，需使用者重新填寫
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    cancelEdit() {
      this.editingId = null
      this.resetForm()
    },

    // 清空表單內容並回到「新建」模式
    resetForm() {
      this.editingId = null
      this.formData.title = ''
      this.formData.description = ''
      this.setRteContent('')
      this.formData.multiDay = false
      this.formData.activityDate = ''
      this.formData.endDate = ''
      this.formData.startTime = ''
      this.formData.endTime = ''
      this.formData.regDeadlineDate = ''
      this.formData.regDeadlineTime = ''
      this.formData.location = ''
      this.formData.speaker = ''
      this.formData.activityType = ''
      this.formData.points = 2
      this.formData.maxParticipants = 50
      this.formData.needsReflection = false
      this.formData.reflectionPoints = 0
      this.formData.reflectionDays = 14
      this.formData.ucanKeywords = []
      this.formData.usrKeywords = []
      this.formData.sdgsKeywords = []
      this.formData.customKeywords = ''
      this.formData.competencyPercentages = [0, 0, 0, 0, 0, 0]
      this.formData.noEquipment = true
      this.formData.equipmentDate = ''
      this.formData.equipmentTime = ''
    }
  }
}
</script>

<style scoped>
.create-activity-page {
  min-height: 100vh;
  background: #f5f6fa;
  padding: 20px;
}

/* 無權限提示 */
.no-permission {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
}

.error-card {
  background: white;
  padding: 60px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  max-width: 500px;
}

.error-icon {
  font-size: 72px;
  margin-bottom: 20px;
}

.error-card h2 {
  color: #dc3545;
  margin-bottom: 15px;
}

.error-card p {
  color: #666;
  margin-bottom: 30px;
}

.back-btn {
  display: inline-block;
  padding: 12px 30px;
  background: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.3s;
}

.back-btn:hover {
  background: #0056b3;
}

/* 主容器 */
.create-activity-container {
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

h1 {
  color: #333;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e9ecef;
}

/* 表單區塊 */
.form-section {
  margin-bottom: 40px;
  padding: 25px;
  background: #f8f9fa;
  border-radius: 8px;
}

.section-title {
  color: #495057;
  font-size: 20px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #dee2e6;
}

/* 表單網格 */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.col-span-2 {
  grid-column: span 2;
}

/* 表單元素 */
.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
  font-size: 14px;
}

.required {
  color: #dc3545;
}

.form-input {
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s;
}

.form-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
}

.disabled-input {
  background-color: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}

.field-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #6c757d;
}

.reg-note {
  margin: 6px 0 0;
  padding: 10px 12px;
  background: #eef6ff;
  border: 1px solid #cfe4fb;
  border-radius: 6px;
  color: #2c5aa0;
  font-size: 14px;
}

textarea.form-input {
  resize: vertical;
  min-height: 100px;
}

/* 活動簡述及目標 富文本編輯器 */
.rte {
  border: 1px solid #ced4da;
  border-radius: 6px;
  overflow: hidden;
  transition: all 0.3s;
}
.rte:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
}
.rte-toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
}
.rte-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 6px;
  border: 1px solid #ced4da;
  border-radius: 5px;
  background: #fff;
  color: #333;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
}
.rte-btn:hover {
  background: #e9f2ff;
  border-color: #007bff;
}
.rte-btn-text {
  font-size: 13px;
}
.rte-color-btn {
  position: relative;
  font-weight: 700;
}
.rte-color-a {
  border-bottom: 3px solid #e53935;
  line-height: 1;
}
.rte-color-input {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.rte-sep {
  width: 1px;
  align-self: stretch;
  margin: 2px 4px;
  background: #dee2e6;
}
.rte-editor {
  display: block;
  width: 100%;
  min-height: 120px;
  max-height: 360px;
  overflow-y: auto;
  padding: 10px 12px;
  border: none;
  border-radius: 0;
  font-size: 14px;
  line-height: 1.7;
  outline: none;
}
.rte-editor:focus {
  outline: none;
  box-shadow: none;
}
.rte-editor.is-empty:not(:focus)::before {
  content: attr(data-placeholder);
  color: #9ca3af;
  pointer-events: none;
}
.rte-editor a {
  color: #007bff;
  text-decoration: underline;
}

/* 時間輸入 */
.time-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
}

.time-separator {
  color: #6c757d;
}

/* 關鍵字區塊 */
.keyword-group {
  margin-bottom: 25px;
}

.keyword-group h4 {
  color: #495057;
  font-size: 16px;
  margin-bottom: 15px;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  color: #495057;
}

.checkbox-label input[type="checkbox"] {
  margin-right: 8px;
}

.checkbox-label:hover {
  color: #007bff;
}

.custom-keywords {
  margin-top: 20px;
}

/* 核心能力 */
.competency-section {
  margin-top: 20px;
}

.competency-grid {
  display: grid;
  gap: 20px;
}

.competency-item {
  display: grid;
  grid-template-columns: 1fr 120px;
  gap: 20px;
  align-items: center;
  padding: 15px;
  background: white;
  border-radius: 6px;
}

.comp-name {
  font-weight: 600;
  color: #333;
  display: block;
  margin-bottom: 5px;
}

.comp-desc {
  font-size: 12px;
  color: #6c757d;
  display: block;
}

.percentage-input {
  display: flex;
  align-items: center;
  gap: 5px;
}

.percentage-input input {
  width: 80px;
  text-align: center;
}

.percentage-symbol {
  color: #6c757d;
  font-weight: 500;
}

.percentage-total {
  margin-top: 20px;
  padding: 15px;
  border-radius: 6px;
  text-align: center;
  font-weight: 600;
  font-size: 18px;
}

.percentage-total.error {
  background: #ffebee;
  color: #c62828;
}

.percentage-total.success {
  background: #e8f5e9;
  color: #2e7d32;
}

.error-msg {
  font-size: 14px;
  font-weight: normal;
  margin-left: 10px;
}

/* 單選按鈕組 */
.radio-group {
  display: flex;
  gap: 20px;
  margin-top: 10px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
}

.radio-label input[type="radio"] {
  margin-right: 8px;
}

/* 設備借用 */
.equipment-group {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.date-inputs {
  display: flex;
  gap: 15px;
}

.mt-2 {
  margin-top: 15px;
}

/* 審核狀態 */
.approval-status {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 15px;
}

.status-item {
  padding: 15px;
  background: white;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-label {
  color: #495057;
  font-weight: 500;
}

.status-value {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.status-pending {
  background: #fff3e0;
  color: #f57c00;
}

.status-approved {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-rejected {
  background: #ffebee;
  color: #c62828;
}

.approval-note {
  color: #6c757d;
  font-size: 14px;
  font-style: italic;
  text-align: center;
}

/* 流程說明 */
.info-section {
  background: #e7f3ff;
}

.process-list {
  margin-left: 20px;
  color: #495057;
}

.process-list li {
  margin-bottom: 10px;
  line-height: 1.6;
}

/* 按鈕 */
.form-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
  padding-top: 30px;
  border-top: 2px solid #e9ecef;
}

.btn-primary,
.btn-secondary {
  padding: 12px 40px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,123,255,0.3);
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108,117,125,0.3);
}

/* 編輯中提示 */
.editing-banner {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 25px;
}

.editing-banner-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  color: #8a6d00;
  font-weight: 600;
}

.cancel-edit-btn {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 14px;
}

.cancel-edit-btn:hover {
  background: #5a6268;
}

.editing-note {
  margin: 8px 0 0;
  font-size: 13px;
  color: #a17c00;
}

/* ===== 活動申請追蹤 ===== */
.tracking-section {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 3px solid #e9ecef;
}

.tracking-title {
  color: #333;
  margin-bottom: 8px;
}

.tracking-hint {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 20px;
}

.tracking-loading,
.tracking-empty {
  text-align: center;
  padding: 30px;
  color: #6c757d;
}

.tracking-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tracking-card {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-left: 4px solid #ced4da;
  border-radius: 8px;
  padding: 18px 20px;
}

.tracking-card.draft { border-left-color: #6c757d; }
.tracking-card.pending { border-left-color: #ffc107; }
.tracking-card.approved { border-left-color: #28a745; }
.tracking-card.rejected { border-left-color: #dc3545; }

.tracking-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.tracking-head h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.tracking-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: bold;
  white-space: nowrap;
}

.tracking-badge.draft { background: #e9ecef; color: #495057; }
.tracking-badge.pending { background: #fff3cd; color: #856404; }
.tracking-badge.approved { background: #d4edda; color: #155724; }
.tracking-badge.rejected { background: #f8d7da; color: #721c24; }

.tracking-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  color: #555;
  font-size: 14px;
  margin-bottom: 8px;
}

.tracking-desc {
  color: #666;
  font-size: 14px;
  margin: 8px 0;
  line-height: 1.5;
}

.tracking-comment {
  background: #eef6ff;
  border: 1px solid #cfe4fb;
  border-radius: 6px;
  padding: 10px 12px;
  color: #2c5aa0;
  font-size: 14px;
  margin: 10px 0;
}

.tracking-comment.rejected {
  background: #fdecea;
  border-color: #f5c2c0;
  color: #a12622;
}

.tracking-actions {
  margin-top: 10px;
}

.btn-edit {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}

.btn-edit:hover { background: #0056b3; }
.btn-edit.active { background: #17a2b8; }

.tracking-locked {
  color: #6c757d;
  font-size: 14px;
  font-style: italic;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .create-activity-container {
    padding: 20px;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .col-span-2 {
    grid-column: span 1;
  }
  
  .checkbox-grid {
    grid-template-columns: 1fr;
  }
  
  .competency-item {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .approval-status {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}

/* 時間防呆警示 */
.time-warning {
  grid-column: 1 / -1;
  margin: 0;
  padding: 8px 12px;
  background: #fdecea;
  border: 1px solid #f5c6cb;
  color: #b71c1c;
  border-radius: 6px;
  font-size: 14px;
}

/* 活動時間 時/分 下拉（24 小時制） */
.time-picker { display: inline-flex; align-items: center; gap: 4px; }
.time-sel { width: auto; min-width: 64px; }
.time-picker .colon { font-weight: 600; }

/* 預覽頁面按鈕 */
.btn-preview {
  padding: 12px 28px;
  border: 1px solid #1976d2;
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
}
.btn-preview:hover { background: #bbdefb; }

/* 活動頁面預覽視窗 */
.preview-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px;
}
.preview-modal {
  background: #fff; border-radius: 10px; width: min(760px, 96vw);
  max-height: 88vh; overflow-y: auto; box-shadow: 0 8px 30px rgba(0,0,0,0.25);
}
.preview-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; border-bottom: 1px solid #eee; position: sticky; top: 0; background: #fff;
}
.preview-head h3 { margin: 0; font-size: 18px; }
.preview-close {
  border: none; background: transparent; font-size: 20px; cursor: pointer; color: #666; line-height: 1;
}
.preview-note { margin: 0; padding: 10px 24px; font-size: 13px; color: #8a5000; background: #fff4e5; }
.preview-body { padding: 20px 24px; }
.preview-title { font-size: 22px; margin: 0 0 16px; color: #1a1a1a; }
.preview-table { width: 100%; border-collapse: collapse; }
.preview-table th, .preview-table td {
  border: 1px solid #e0e0e0; padding: 10px 12px; text-align: left; vertical-align: top; font-size: 15px;
}
.preview-table th { background: #f5f7fa; width: 130px; white-space: nowrap; font-weight: 600; }
.preview-desc { line-height: 1.7; }
.preview-desc .rich-content { white-space: pre-wrap; }
.preview-actions { padding: 12px 24px 20px; text-align: right; }
</style>